import { KeyboardEventHandler, MouseEventHandler } from 'react';
import { Control, FieldValues, useController } from 'react-hook-form';

import { Props as TextFieldProps } from '../../../../../../components/HookFormControls/TextField';

export interface UseTimerDigitTextField<T extends FieldValues> extends TextFieldProps<T> {
  /**
   * only allows the characters till upperLimit
   * @default 9
   */
  upperLimit?: number;
}

const useTimerDigitTextField = <T extends FieldValues>({
  control,
  name,
  defaultValue,
  upperLimit = 9,
}: UseTimerDigitTextField<T>) => {
  const { field } = useController({
    control,
    name,
    defaultValue,
  });

  const { value, onChange } = field;

  const handleMouseUp: MouseEventHandler<HTMLInputElement> = (event) => {
    event.preventDefault();
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = async (event) => {
    const numVal = +value;

    const target = event.target as HTMLInputElement | HTMLTextAreaElement;

    const form = target.form;
    if (!form) return;

    const formElements = Array.from(form);
    const index = formElements.indexOf(target);

    const isFirstElement = index === 0;
    const isLastElement = index === formElements.length - 1;

    const prevElement = isFirstElement ? null : (formElements[index - 1] as HTMLInputElement);
    const nextElement = isLastElement ? null : (formElements[index + 1] as HTMLInputElement);

    if (event.key === 'Backspace') {
      if (numVal !== 0) {
        onChange('0');
        return;
      }

      if (!prevElement) return;
      prevElement.focus();
      return;
    }

    if (event.key === 'ArrowLeft') {
      if (!prevElement) return;
      prevElement.focus();
      return;
    }

    if (event.key === 'ArrowRight') {
      if (!nextElement) return;
      nextElement.focus();
      return;
    }

    if (event.key === 'ArrowUp') {
      if (value < upperLimit) {
        onChange(`${numVal + 1}`);
        return;
      }

      if (!prevElement) return;

      // TODO: increment prev element's value and set current element's value to 0
      return;
    }

    if (event.key === 'ArrowDown') {
      if (value > 0) {
        onChange(`${numVal - 1}`);
        return;
      }

      if (!nextElement) return;

      // TODO: decrement prev element's value and set current element's value to 9
      return;
    }

    if (+event.key >= 0 || +event.key <= 9) {
      onChange(`${event.key}`);
    }

    if (+target.value >= 0 || +target.value <= upperLimit) {
      if (nextElement) {
        await new Promise((resolve) => setTimeout(resolve, 0));
        nextElement.focus();
      }
    }
  };

  const handleKeyUp: KeyboardEventHandler<HTMLInputElement> = (event) => {
    (event.target as HTMLInputElement).select();
  };

  return { handleMouseUp, handleKeyDown, handleKeyUp };
};

export default useTimerDigitTextField;
